// NobleAssetPassport - Equipment lifecycle timeline visualization
// Features: Vertical timeline, event markers, photos, documents, health score
// Perfect for maintenance history and predictive insights

import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Card, Typography, Space, Tag, Avatar, Image, Divider, Collapse } from 'antd';
import {
  ToolOutlined,
  FileTextOutlined,
  CameraOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  CalendarOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { NobleColors, NobleSpacing, NobleShadows } from '../../../theme/noble-tokens';
import { HealthScore } from '../NobleProgressRing';
import { format } from 'date-fns';

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

export type AssetEventType = 
  | 'installation' 
  | 'maintenance' 
  | 'repair' 
  | 'upgrade' 
  | 'inspection' 
  | 'failure' 
  | 'calibration';

export interface AssetEvent {
  id: string;
  type: AssetEventType;
  date: Date;
  title: string;
  description?: string;
  technician?: string;
  cost?: number;
  photos?: string[];
  documents?: Array<{ name: string; url: string }>;
  downtime?: number; // hours
}

export interface NobleAssetPassportProps {
  equipmentId: string;
  equipmentName: string;
  equipmentType: string;
  manufacturer?: string;
  model?: string;
  serialNumber?: string;
  installationDate: Date;
  photo?: string;
  healthScore: number; // 0-100
  totalRuntime: number; // hours
  mtbf?: number; // Mean Time Between Failures (hours)
  totalMaintenanceCost: number;
  availability: number; // percentage
  events: AssetEvent[];
  predictedNextMaintenance?: Date;
  riskLevel?: 'low' | 'medium' | 'high';
  className?: string;
}

// Event type configuration
const EVENT_CONFIG = {
  installation: {
    icon: <ToolOutlined />,
    color: NobleColors.primary.commandBlue,
    label: 'Installation',
  },
  maintenance: {
    icon: <ToolOutlined />,
    color: NobleColors.semantic.operationalGreen,
    label: 'Maintenance',
  },
  repair: {
    icon: <WarningOutlined />,
    color: NobleColors.semantic.warningAmber,
    label: 'Repair',
  },
  upgrade: {
    icon: <CheckCircleOutlined />,
    color: NobleColors.accent.precisionTeal,
    label: 'Upgrade',
  },
  inspection: {
    icon: <FileTextOutlined />,
    color: NobleColors.primary.commandBlue,
    label: 'Inspection',
  },
  failure: {
    icon: <WarningOutlined />,
    color: NobleColors.semantic.criticalRed,
    label: 'Failure',
  },
  calibration: {
    icon: <CheckCircleOutlined />,
    color: NobleColors.accent.precisionTeal,
    label: 'Calibration',
  },
};

// Styled Components
const PassportContainer = styled(Card)`
  border-radius: 16px;
  box-shadow: ${NobleShadows['elevation-2']};
  overflow: hidden;
`;

const PassportHeader = styled.div`
  background: linear-gradient(135deg, ${NobleColors.accent.steelGrayDark} 0%, ${NobleColors.accent.steelGrayUltraDark} 100%);
  padding: ${NobleSpacing.xl};
  color: white;
`;

const HeaderTop = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${NobleSpacing.xl};
  margin-bottom: ${NobleSpacing.lg};
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
`;

const EquipmentPhoto = styled.div`
  width: 160px;
  height: 160px;
  border-radius: 12px;
  overflow: hidden;
  border: 4px solid rgba(255, 255, 255, 0.2);
  flex-shrink: 0;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const HeaderInfo = styled.div`
  flex: 1;
`;

const EquipmentTitle = styled(Title)`
  &&& {
    color: white;
    margin: 0 0 ${NobleSpacing.xs} 0;
    font-size: 28px;
  }
`;

const EquipmentMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${NobleSpacing.md};
  margin-top: ${NobleSpacing.md};
  font-size: 14px;
  opacity: 0.9;
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${NobleSpacing.lg};
  margin-top: ${NobleSpacing.lg};
`;

const MetricCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  padding: ${NobleSpacing.lg};
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const MetricValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: white;
  margin-bottom: ${NobleSpacing.xxs};
`;

const MetricLabel = styled.div`
  font-size: 12px;
  opacity: 0.8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TimelineContainer = styled.div`
  position: relative;
  padding: ${NobleSpacing.xl} ${NobleSpacing.lg};
`;

const TimelineLine = styled.div`
  position: absolute;
  left: 60px;
  top: 0;
  bottom: 0;
  width: 3px;
  background: linear-gradient(180deg, ${NobleColors.primary.commandBlue} 0%, ${NobleColors.accent.steelGray} 100%);
  
  @media (max-width: 768px) {
    left: 30px;
  }
`;

const TimelineEvent = styled.div<{ $type: AssetEventType }>`
  position: relative;
  margin-bottom: ${NobleSpacing.xl};
  padding-left: 100px;
  
  @media (max-width: 768px) {
    padding-left: 70px;
  }
`;

const EventMarker = styled.div<{ $color: string }>`
  position: absolute;
  left: 46px;
  top: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 0 4px ${NobleColors.background.level1Light}, 0 2px 8px rgba(0,0,0,0.15);
  z-index: 2;
  
  @media (max-width: 768px) {
    left: 16px;
  }
`;

const EventCard = styled(Card)`
  border-radius: 12px;
  box-shadow: ${NobleShadows['elevation-1']};
  
  .ant-card-head {
    background: ${NobleColors.background.level0Light};
    border-bottom: 2px solid ${NobleColors.background.level2Light};
  }
  
  &:hover {
    box-shadow: ${NobleShadows['elevation-2']};
    transform: translateY(-2px);
    transition: all 0.2s ease-out;
  }
`;

const EventHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${NobleSpacing.md};
`;

const EventTitle = styled(Text)`
  &&& {
    font-size: 16px;
    font-weight: 600;
    color: ${NobleColors.accent.steelGrayDark};
  }
`;

const EventDate = styled(Text)`
  &&& {
    font-size: 13px;
    color: ${NobleColors.accent.steelGray};
    font-family: 'JetBrains Mono', monospace;
  }
`;

const EventDetails = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${NobleSpacing.md};
  margin-top: ${NobleSpacing.sm};
  font-size: 13px;
  color: ${NobleColors.accent.steelGray};
`;

const PhotoGallery = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  gap: ${NobleSpacing.sm};
  margin-top: ${NobleSpacing.md};
`;

const DocumentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${NobleSpacing.xs};
  margin-top: ${NobleSpacing.md};
`;

const DocumentItem = styled.a`
  display: flex;
  align-items: center;
  gap: ${NobleSpacing.xs};
  padding: ${NobleSpacing.sm};
  background: ${NobleColors.background.level0Light};
  border-radius: 6px;
  color: ${NobleColors.primary.commandBlue};
  text-decoration: none;
  
  &:hover {
    background: ${NobleColors.background.level2Light};
    transform: translateX(4px);
    transition: all 0.2s ease-out;
  }
`;

const PredictiveInsights = styled.div`
  background: linear-gradient(135deg, ${NobleColors.accent.precisionTeal} 0%, ${NobleColors.accent.precisionTealDark} 100%);
  padding: ${NobleSpacing.xl};
  border-radius: 12px;
  color: white;
  margin-top: ${NobleSpacing.lg};
`;

// Helper functions
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatRuntime = (hours: number): string => {
  if (hours < 24) return `${Math.round(hours)}h`;
  const days = Math.floor(hours / 24);
  if (days < 365) return `${days} days`;
  const years = Math.floor(days / 365);
  const remainingDays = days % 365;
  return `${years}y ${remainingDays}d`;
};

// Main Component
export const NobleAssetPassport: React.FC<NobleAssetPassportProps> = ({
  equipmentId,
  equipmentName,
  equipmentType,
  manufacturer,
  model,
  serialNumber,
  installationDate,
  photo,
  healthScore,
  totalRuntime,
  mtbf,
  totalMaintenanceCost,
  availability,
  events,
  predictedNextMaintenance,
  riskLevel = 'low',
  className,
}) => {
  // Sort events by date (newest first)
  const sortedEvents = [...events].sort((a, b) => b.date.getTime() - a.date.getTime());
  
  // Calculate equipment age
  const ageInDays = Math.floor((Date.now() - installationDate.getTime()) / (1000 * 60 * 60 * 24));
  const ageInYears = Math.floor(ageInDays / 365);

  return (
    <PassportContainer className={className}>
      {/* Header */}
      <PassportHeader>
        <HeaderTop>
          {photo && (
            <EquipmentPhoto>
              <img src={photo} alt={equipmentName} />
            </EquipmentPhoto>
          )}
          
          <HeaderInfo>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <div>
                <Tag color="blue" style={{ marginBottom: NobleSpacing.xs }}>
                  {equipmentId}
                </Tag>
                <EquipmentTitle level={2}>{equipmentName}</EquipmentTitle>
              </div>
              
              <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: '16px' }}>
                {equipmentType}
              </Text>
              
              <EquipmentMeta>
                {manufacturer && <span>🏭 {manufacturer}</span>}
                {model && <span>📋 Model: {model}</span>}
                {serialNumber && <span>🔢 S/N: {serialNumber}</span>}
                <span>📅 Age: {ageInYears} years ({ageInDays} days)</span>
              </EquipmentMeta>
            </Space>
          </HeaderInfo>
          
          <div>
            <HealthScore value={healthScore} size="medium" />
          </div>
        </HeaderTop>
        
        {/* Key Metrics */}
        <MetricsGrid>
          <MetricCard>
            <MetricValue>{formatRuntime(totalRuntime)}</MetricValue>
            <MetricLabel>Total Runtime</MetricLabel>
          </MetricCard>
          
          {mtbf && (
            <MetricCard>
              <MetricValue>{formatRuntime(mtbf)}</MetricValue>
              <MetricLabel>MTBF</MetricLabel>
            </MetricCard>
          )}
          
          <MetricCard>
            <MetricValue>{formatCurrency(totalMaintenanceCost)}</MetricValue>
            <MetricLabel>Maintenance Cost</MetricLabel>
          </MetricCard>
          
          <MetricCard>
            <MetricValue>{availability}%</MetricValue>
            <MetricLabel>Availability</MetricLabel>
          </MetricCard>
        </MetricsGrid>
      </PassportHeader>
      
      {/* Predictive Insights */}
      {predictedNextMaintenance && (
        <PredictiveInsights>
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <Title level={4} style={{ color: 'white', margin: 0 }}>
              🔮 Predictive Insights
            </Title>
            <Paragraph style={{ color: 'rgba(255,255,255,0.9)', margin: 0 }}>
              <CalendarOutlined /> Next maintenance predicted: <strong>{format(predictedNextMaintenance, 'MMM dd, yyyy')}</strong>
            </Paragraph>
            <Paragraph style={{ color: 'rgba(255,255,255,0.9)', margin: 0 }}>
              <WarningOutlined /> Failure risk level: <Tag color={riskLevel === 'high' ? 'red' : riskLevel === 'medium' ? 'orange' : 'green'}>{riskLevel.toUpperCase()}</Tag>
            </Paragraph>
          </Space>
        </PredictiveInsights>
      )}
      
      {/* Timeline */}
      <TimelineContainer>
        <Title level={3} style={{ marginBottom: NobleSpacing.xl }}>
          Equipment Timeline ({sortedEvents.length} events)
        </Title>
        
        <TimelineLine />
        
        {sortedEvents.map((event) => {
          const config = EVENT_CONFIG[event.type];
          
          return (
            <TimelineEvent key={event.id} $type={event.type}>
              <EventMarker $color={config.color}>
                {config.icon}
              </EventMarker>
              
              <EventCard
                size="small"
                title={
                  <EventHeader>
                    <div>
                      <Tag color={config.color}>{config.label}</Tag>
                      <EventTitle>{event.title}</EventTitle>
                    </div>
                    <EventDate>{format(event.date, 'MMM dd, yyyy')}</EventDate>
                  </EventHeader>
                }
              >
                {event.description && (
                  <Paragraph style={{ marginBottom: NobleSpacing.sm }}>
                    {event.description}
                  </Paragraph>
                )}
                
                <EventDetails>
                  {event.technician && (
                    <span><UserOutlined /> {event.technician}</span>
                  )}
                  {event.cost && (
                    <span><DollarOutlined /> {formatCurrency(event.cost)}</span>
                  )}
                  {event.downtime && (
                    <span><ClockCircleOutlined /> Downtime: {event.downtime}h</span>
                  )}
                </EventDetails>
                
                {event.photos && event.photos.length > 0 && (
                  <PhotoGallery>
                    <Image.PreviewGroup>
                      {event.photos.map((photo, idx) => (
                        <Image
                          key={idx}
                          src={photo}
                          alt={`Photo ${idx + 1}`}
                          style={{ borderRadius: '6px', objectFit: 'cover' }}
                          height={80}
                        />
                      ))}
                    </Image.PreviewGroup>
                  </PhotoGallery>
                )}
                
                {event.documents && event.documents.length > 0 && (
                  <DocumentList>
                    {event.documents.map((doc, idx) => (
                      <DocumentItem key={idx} href={doc.url} target="_blank" rel="noopener noreferrer">
                        <FileTextOutlined />
                        {doc.name}
                      </DocumentItem>
                    ))}
                  </DocumentList>
                )}
              </EventCard>
            </TimelineEvent>
          );
        })}
      </TimelineContainer>
    </PassportContainer>
  );
};

